import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { entsperre, entsperreMitRecovery } from 'core/features/sicherheit/sicherheitSlice';
import { leseKeyfile, baueKeyfileInhalt } from 'core/features/sicherheit/crypto';
import { ladeTextHerunter } from 'core/utils/format';

/**
 * Entsperr-Ansicht des Tresors: Passphrase (+ optionale Schluesseldatei) oder
 * alternativ Wiederherstellung ueber den Recovery-Code mit neuer Passphrase.
 */
export default function VaultUnlock() {
  const [modus, setModus] = useState('normal'); // normal | recovery

  return (
    <div className="mx-auto max-w-md">
      <div className="rounded-2xl border border-line bg-paper p-6 shadow-sm">
        <div className="mb-4 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-600/10 text-2xl">🔒</div>
          <h1 className="font-serif text-2xl font-semibold text-ink">Gesicherten Modus entsperren</h1>
          <p className="mt-1 text-sm text-ink-muted">Gib deine Passphrase ein, um deine gesicherten Bereiche zu öffnen.</p>
        </div>

        {modus === 'normal' ? (
          <NormalFormular onRecovery={() => setModus('recovery')} />
        ) : (
          <RecoveryFormular onAbbrechen={() => setModus('normal')} />
        )}
      </div>
    </div>
  );
}

/** Liest eine ausgewaehlte Schluesseldatei in Roh-Bytes (oder null bei Fehler). */
async function keyfileAusEvent(event) {
  const datei = event.target.files?.[0];
  if (!datei) return null;
  try {
    return leseKeyfile(await datei.text());
  } catch (ex) {
    return null;
  }
}

/** Entsperren per Passphrase (+ optionaler Schluesseldatei). */
function NormalFormular({ onRecovery }) {
  const dispatch = useDispatch();
  const [passphrase, setPassphrase] = useState('');
  const [keyfileBytes, setKeyfileBytes] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const absenden = async (e) => {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(entsperre({ passphrase, keyfileBytes })).unwrap();
    } catch (ex) {
      setFehler('Entsperren fehlgeschlagen – Passphrase oder Schlüsseldatei falsch.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <form onSubmit={absenden} className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Passphrase</label>
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          autoComplete="current-password"
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">
          Schlüsseldatei <span className="font-normal text-ink-muted">(falls beim Einrichten aktiviert)</span>
        </label>
        <input
          type="file"
          accept=".key,text/plain"
          onChange={async (e) => setKeyfileBytes(await keyfileAusEvent(e))}
          className="w-full text-sm text-ink-muted file:mr-3 file:rounded-lg file:border-0 file:bg-paper-sunken file:px-3 file:py-1.5 file:text-sm file:text-ink"
        />
      </div>

      {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

      <button
        type="submit"
        disabled={laeuft}
        className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
      >
        {laeuft ? 'Entsperre…' : 'Entsperren'}
      </button>

      <button type="button" onClick={onRecovery} className="w-full text-center text-sm text-brand-600 hover:underline">
        Passphrase vergessen? Mit Wiederherstellungs-Code öffnen
      </button>
    </form>
  );
}

/** Wiederherstellung ueber den Recovery-Code und Setzen einer neuen Passphrase. */
function RecoveryFormular({ onAbbrechen }) {
  const dispatch = useDispatch();
  const [recoveryCode, setRecoveryCode] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [mitKeyfile, setMitKeyfile] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const absenden = async (e) => {
    e.preventDefault();
    if (passphrase.length < 8 || passphrase !== wiederholung) {
      setFehler('Neue Passphrase: mindestens 8 Zeichen und beide Felder gleich.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      const { keyfileBase64 } = await dispatch(
        entsperreMitRecovery({ recoveryCode, neuePassphrase: passphrase, mitKeyfile })
      ).unwrap();
      if (keyfileBase64) {
        ladeTextHerunter('tresor.key', baueKeyfileInhalt(keyfileBase64));
      }
    } catch (ex) {
      setFehler('Wiederherstellung fehlgeschlagen – Code prüfen.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <form onSubmit={absenden} className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Wiederherstellungs-Code</label>
        <textarea
          value={recoveryCode}
          onChange={(e) => setRecoveryCode(e.target.value)}
          rows={2}
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 font-mono text-sm text-ink focus:border-brand-600 focus:outline-none"
          placeholder="XXXX-XXXX-XXXX-…"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Neue Passphrase</label>
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Neue Passphrase wiederholen</label>
        <input
          type="password"
          value={wiederholung}
          onChange={(e) => setWiederholung(e.target.value)}
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
        />
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={mitKeyfile} onChange={(e) => setMitKeyfile(e.target.checked)} className="h-4 w-4 accent-brand-600" />
        Neue Schlüsseldatei als zweiten Faktor erzeugen
      </label>

      {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

      <button
        type="submit"
        disabled={laeuft}
        className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
      >
        {laeuft ? 'Stelle wieder her…' : 'Wiederherstellen & entsperren'}
      </button>
      <button type="button" onClick={onAbbrechen} className="w-full text-center text-sm text-ink-muted hover:underline">
        Zurück
      </button>
    </form>
  );
}
